import React from "react";

export function PageHeader({ eyebrow, title, subtitle, actions, testid }) {
  return (
    <div className="px-4 sm:px-10 pt-6 sm:pt-10 pb-5 sm:pb-6 border-b border-[var(--line)] bg-[var(--surface)]" data-testid={testid}>
      <div className="flex items-end justify-between gap-4 sm:gap-6 flex-wrap">
        <div>
          {eyebrow && (
            <div className="text-[11px] uppercase tracking-[0.18em] text-[var(--muted)] mb-2 font-semibold">{eyebrow}</div>
          )}
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[var(--ink)] leading-[1.05] tracking-tight">{title}</h1>
          {subtitle && <p className="mt-2 sm:mt-3 text-[var(--ink-2)] max-w-2xl text-sm">{subtitle}</p>}
        </div>
        {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
      </div>
    </div>
  );
}

export function Kpi({ label, value, suffix, accent, testid }) {
  return (
    <div className="rise bg-[var(--surface)] border border-[var(--line)] p-6 relative overflow-hidden" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] font-semibold">{label}</div>
      <div className="mt-3 flex items-baseline gap-2">
        <div className={`num text-3xl font-semibold ${accent ? "text-[var(--accent)]" : "text-[var(--ink)]"}`}>{value}</div>
        {suffix && <div className="text-xs text-[var(--muted)] font-medium">{suffix}</div>}
      </div>
    </div>
  );
}

export function Section({ title, right, children, testid }) {
  return (
    <div className="bg-[var(--surface)] border border-[var(--line)]" data-testid={testid}>
      <div className="flex items-center justify-between gap-2 px-3 sm:px-5 py-3 border-b border-[var(--line)] flex-wrap">
        <div className="text-[11px] uppercase tracking-[0.16em] text-[var(--ink-2)] font-semibold">{title}</div>
        {right}
      </div>
      <div>{children}</div>
    </div>
  );
}

export function Btn({ children, onClick, variant = "primary", testid, type = "button", disabled }) {
  const base = "inline-flex items-center gap-2 px-4 h-9 text-[13px] font-medium rounded-sm transition-colors disabled:opacity-50";
  const styles = {
    primary: "bg-[var(--ink)] text-white hover:bg-black",
    accent: "bg-[var(--accent)] text-white hover:brightness-95",
    ghost: "bg-transparent text-[var(--ink)] hover:bg-[var(--surface-2)] border border-[var(--line)]",
    danger: "bg-transparent text-[var(--negative)] hover:bg-red-50 border border-red-200",
  };
  return (
    <button type={type} data-testid={testid} disabled={disabled} onClick={onClick} className={`${base} ${styles[variant]}`}>
      {children}
    </button>
  );
}

export const Input = React.forwardRef(function Input(props, ref) {
  return (
    <input
      ref={ref}
      {...props}
      className={`w-full h-9 px-3 text-[13.5px] bg-[var(--surface)] border border-[var(--line-2)] rounded-sm focus:outline-none focus:border-[var(--ink)] ${props.className || ""}`}
    />
  );
});

export function Select({ options, value, onChange, testid, placeholder, disabled = false }) {
  return (
    <select
      data-testid={testid}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className="w-full h-9 px-3 text-[13.5px] bg-[var(--surface)] border border-[var(--line-2)] rounded-sm focus:outline-none focus:border-[var(--ink)] disabled:bg-[var(--surface-2)] disabled:text-[var(--muted)] disabled:cursor-not-allowed"
    >
      {placeholder && <option value="">{placeholder}</option>}
      {options.map((o) => (
        <option key={typeof o === "string" ? o : o.value} value={typeof o === "string" ? o : o.value}>
          {typeof o === "string" ? o : o.label}
        </option>
      ))}
    </select>
  );
}

/**
 * ComboSelect — Select with an "Add new…" sentinel that opens a small inline
 * input, letting the operator type a custom value while keeping the same
 * native-Select look as Gateway / Partner dropdowns.
 *   - `options`: string[] (predefined)
 *   - `value`: current value (may be a custom one not in options)
 *   - `onChange(v)`: fired with selected/typed value
 */
export function ComboSelect({ options, value, onChange, testid, placeholder, addLabel = "+ Add new…", disabled = false }) {
  const ADD = "__add_new__";
  const [adding, setAdding] = React.useState(false);
  const [draft, setDraft] = React.useState("");
  const opts = Array.isArray(options) ? options.filter(Boolean) : [];
  const known = new Set(opts);
  // If the current value isn't in the options list, surface it as a one-off
  // entry so the dropdown doesn't show as empty.
  const merged = value && !known.has(value) ? [value, ...opts] : opts;
  const handleSelect = (v) => {
    if (v === ADD) {
      setAdding(true);
      setDraft("");
      return;
    }
    onChange(v);
  };
  const commitDraft = () => {
    const v = draft.trim();
    if (v) onChange(v);
    setAdding(false);
    setDraft("");
  };
  if (adding) {
    return (
      <div className="flex gap-2" data-testid={testid ? `${testid}-add-row` : undefined}>
        <input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); commitDraft(); }
            if (e.key === "Escape") { setAdding(false); setDraft(""); }
          }}
          placeholder="Type new value…"
          data-testid={testid ? `${testid}-add-input` : undefined}
          className="flex-1 h-9 px-3 text-[13.5px] bg-[var(--surface)] border border-[var(--line-2)] rounded-sm focus:outline-none focus:border-[var(--ink)]"
        />
        <button
          type="button"
          data-testid={testid ? `${testid}-add-confirm` : undefined}
          onClick={commitDraft}
          className="h-9 px-3 text-[12px] font-medium rounded-sm bg-[var(--ink)] text-white hover:bg-black"
        >Add</button>
        <button
          type="button"
          onClick={() => { setAdding(false); setDraft(""); }}
          className="h-9 px-3 text-[12px] font-medium rounded-sm border border-[var(--line)] text-[var(--ink)] hover:bg-[var(--surface-2)]"
        >Cancel</button>
      </div>
    );
  }
  return (
    <select
      data-testid={testid}
      value={value ?? ""}
      onChange={(e) => handleSelect(e.target.value)}
      disabled={disabled}
      className="w-full h-9 px-3 text-[13.5px] bg-[var(--surface)] border border-[var(--line-2)] rounded-sm focus:outline-none focus:border-[var(--ink)] disabled:bg-[var(--surface-2)] disabled:text-[var(--muted)] disabled:cursor-not-allowed"
    >
      {placeholder && <option value="">{placeholder}</option>}
      {merged.map((o) => <option key={o} value={o}>{o}</option>)}
      {!disabled && <option value={ADD}>{addLabel}</option>}
    </select>
  );
}

/**
 * SearchableSelect — Select with a built-in filter input. Same look as `Select`
 * (h-9, cream surface, var(--line-2) border) but renders as a popover with a
 * type-to-filter box on top of the option list. Use whenever the option count
 * exceeds ~15 (currently: merchant picker on VolumeInput).
 *   - `options`: Array<{ value: string, label: string }>
 *   - `value`: currently selected value
 *   - `onChange(v)`: fires with the chosen option's value
 *   - `searchPlaceholder`: filter input placeholder (defaults to "Search…")
 */
export function SearchableSelect({ options, value, onChange, testid, placeholder, searchPlaceholder = "Search…", disabled = false }) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const rootRef = React.useRef(null);
  const inputRef = React.useRef(null);
  const opts = Array.isArray(options) ? options : [];
  const selected = opts.find((o) => o.value === value);
  const q = query.trim().toLowerCase();
  const filtered = q ? opts.filter((o) => (o.label || "").toLowerCase().includes(q) || (o.value || "").toLowerCase().includes(q)) : opts;

  // Close on outside click + Escape
  React.useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    // Focus the filter input on open so user can type immediately
    setTimeout(() => inputRef.current?.focus(), 0);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Reset query when closing so re-opening starts fresh
  React.useEffect(() => { if (!open) setQuery(""); }, [open]);

  return (
    <div ref={rootRef} className="relative" data-testid={testid}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((v) => !v)}
        data-testid={testid ? `${testid}-trigger` : undefined}
        className="w-full h-9 px-3 text-[13.5px] bg-[var(--surface)] border border-[var(--line-2)] rounded-sm focus:outline-none focus:border-[var(--ink)] disabled:bg-[var(--surface-2)] disabled:text-[var(--muted)] disabled:cursor-not-allowed flex items-center justify-between text-left"
      >
        <span className={`truncate ${selected ? "text-[var(--ink)]" : "text-[var(--muted)]"}`}>
          {selected ? selected.label : (placeholder || "Select…")}
        </span>
        <span className="ml-2 text-[10px] text-[var(--muted)]">▾</span>
      </button>
      {open && (
        <div
          className="absolute z-50 mt-1 w-full bg-[var(--surface)] border border-[var(--line-2)] rounded-sm shadow-lg"
          data-testid={testid ? `${testid}-popover` : undefined}
        >
          <div className="p-2 border-b border-[var(--line)]">
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={searchPlaceholder}
              data-testid={testid ? `${testid}-search` : undefined}
              className="w-full h-8 px-2 text-[13px] bg-[var(--surface)] border border-[var(--line-2)] rounded-sm focus:outline-none focus:border-[var(--ink)]"
            />
          </div>
          <div className="max-h-64 overflow-y-auto py-1" data-testid={testid ? `${testid}-list` : undefined}>
            {filtered.length === 0 && (
              <div className="px-3 py-2 text-[12.5px] text-[var(--muted)] italic">No matches.</div>
            )}
            {filtered.map((o) => (
              <button
                type="button"
                key={o.value}
                onClick={() => { onChange(o.value); setOpen(false); }}
                data-testid={testid ? `${testid}-option-${o.value}` : undefined}
                className={`w-full text-left px-3 py-1.5 text-[13px] hover:bg-[var(--surface-2)] ${o.value === value ? "bg-[var(--surface-2)] font-medium" : ""}`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function Badge({ children, tone = "default", className = "" }) {
  const tones = {
    default: "bg-[var(--surface-2)] text-[var(--ink-2)] border-[var(--line-2)]",
    positive: "bg-emerald-50 text-emerald-800 border-emerald-200",
    warning: "bg-amber-50 text-amber-800 border-amber-200",
    muted: "bg-gray-100 text-gray-600 border-gray-200",
    accent: "bg-orange-50 text-orange-800 border-orange-200",
  };
  return <span className={`inline-flex items-center px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider border rounded-sm ${tones[tone]} ${className}`}>{children}</span>;
}

export function Modal({ open, onClose, title, children, testid, size = "md" }) {
  if (!open) return null;
  const maxW = size === "xl" ? "max-w-5xl" : size === "lg" ? "max-w-3xl" : "max-w-lg";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm p-4" data-testid={testid}>
      <div className={`bg-[var(--surface)] border border-[var(--line-2)] w-full ${maxW} rounded-sm shadow-2xl flex flex-col max-h-[90vh]`}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--line)] shrink-0">
          <div className="font-display text-lg text-[var(--ink)]">{title}</div>
          <button onClick={onClose} data-testid="modal-close" className="text-[var(--muted)] hover:text-[var(--ink)]">✕</button>
        </div>
        <div className="p-5 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

/**
 * MonthPicker — two side-by-side <select>s (Month + Year) that emit a 'YYYY-MM' string.
 * - `value`: current 'YYYY-MM' (or empty)
 * - `onChange(v)`: fired when either dropdown changes; v is 'YYYY-MM' or ''
 * - Year range: minYear..maxYear inclusive. Defaults cover ~10 years back and 5 years forward from now.
 * - `placeholder`: label shown when value is empty (e.g. "Select month")
 * - `testid` optional for test hooks
 */
export function MonthPicker({ value, onChange, minYear, maxYear, placeholder = "Select month", testid }) {
  const now = new Date();
  const thisYear = now.getFullYear();
  const yStart = minYear ?? thisYear - 10;
  const yEnd = maxYear ?? thisYear + 5;
  const [yy, mm] = (value || "").split("-");
  const selYear = yy || "";
  const selMonth = mm || "";

  const months = [
    { v: "01", l: "Jan" }, { v: "02", l: "Feb" }, { v: "03", l: "Mar" },
    { v: "04", l: "Apr" }, { v: "05", l: "May" }, { v: "06", l: "Jun" },
    { v: "07", l: "Jul" }, { v: "08", l: "Aug" }, { v: "09", l: "Sep" },
    { v: "10", l: "Oct" }, { v: "11", l: "Nov" }, { v: "12", l: "Dec" },
  ];
  const years = [];
  for (let y = yEnd; y >= yStart; y--) years.push(String(y));

  const emit = (newYear, newMonth) => {
    if (newYear && newMonth) onChange(`${newYear}-${newMonth}`);
    else if (!newYear && !newMonth) onChange("");
    else onChange(`${newYear || selYear}-${newMonth || selMonth}`);
  };

  const cls = "h-9 px-2 text-[13.5px] bg-[var(--surface)] border border-[var(--line-2)] rounded-sm focus:outline-none focus:border-[var(--ink)]";

  return (
    <div className="flex gap-2" data-testid={testid}>
      <select
        data-testid={testid ? `${testid}-month` : undefined}
        value={selMonth}
        onChange={(e) => emit(selYear || String(thisYear), e.target.value)}
        className={`${cls} flex-1`}
      >
        <option value="">{placeholder.includes("month") ? "Month" : placeholder}</option>
        {months.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
      </select>
      <select
        data-testid={testid ? `${testid}-year` : undefined}
        value={selYear}
        onChange={(e) => emit(e.target.value, selMonth || "01")}
        className={`${cls} w-24`}
      >
        <option value="">Year</option>
        {years.map((y) => <option key={y} value={y}>{y}</option>)}
      </select>
    </div>
  );
}
